import { readTextBlocks, type TextPiece } from './htmlText.ts';
import { findInText, type TextMatch } from './search.ts';

/**
 * Replacing text in an entry's HTML without a DOM (docs/REPLACE-PLAN.md section 3.2), as
 * the desktop's `search.RichText.replace` does: a block's text is matched across its text
 * nodes, the replacement goes into the node where the match starts (keeping that node's
 * formatting), and the rest of the match is taken out of the nodes it ran into. Only those
 * characters of the HTML change: tags, attributes, links, comments and everything else stay
 * exactly as they were, and the replacement is escaped, so it can never add markup.
 */

/** Text as HTML text: `&`, `<` and `>` escaped. */
export function escapeHtmlText(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

interface Edit {
  readonly from: number;
  readonly to: number;
  readonly insert: string;
}

/** The edits that replace one match in a block's pieces, as `RichText.apply` does. */
function editsFor(pieces: readonly TextPiece[], match: TextMatch, replacement: string): Edit[] {
  const edits: Edit[] = [];
  for (let i = 0; i < pieces.length; i++) {
    const piece = pieces[i]!;
    const pieceEnd = piece.start + piece.text.length;
    if (match.start >= pieceEnd && !(match.start === pieceEnd && i === pieces.length - 1)) continue;
    const from = match.start - piece.start;
    let remaining = match.end - match.start;
    const take = Math.min(remaining, piece.text.length - from);
    edits.push({ from: piece.at[from]!, to: piece.at[from + take]!, insert: escapeHtmlText(replacement) });
    remaining -= take;
    for (let j = i + 1; j < pieces.length && remaining > 0; j++) {
      const next = pieces[j]!;
      const cut = Math.min(remaining, next.text.length);
      edits.push({ from: next.at[0]!, to: next.at[cut]!, insert: '' });
      remaining -= cut;
    }
    break;
  }
  return edits;
}

export interface HtmlReplaced {
  readonly html: string;
  readonly replaced: number;
}

/** Every match of `pattern` in `html`'s text replaced with `replacement`. */
export function replaceInHtml(html: string, pattern: RegExp, replacement: string): HtmlReplaced {
  const edits: Edit[] = [];
  let replaced = 0;
  for (const block of readTextBlocks(html)) {
    for (const match of findInText(block.text, pattern)) {
      edits.push(...editsFor(block.pieces, match, replacement));
      replaced++;
    }
  }
  if (replaced === 0) return { html, replaced };
  // Back to front, so earlier positions stay right; matches never overlap.
  edits.sort((a, b) => b.from - a.from);
  let out = html;
  for (const edit of edits) out = out.slice(0, edit.from) + edit.insert + out.slice(edit.to);
  return { html: out, replaced };
}

/** `PlainText.replace` for every match: plain text, such as a title or a field's value. */
export function replaceInText(text: string, pattern: RegExp, replacement: string): { text: string; replaced: number } {
  const matches = findInText(text, pattern);
  let out = text;
  for (const m of [...matches].reverse()) out = out.slice(0, m.start) + replacement + out.slice(m.end);
  return { text: out, replaced: matches.length };
}
